import React from 'react';
import { motion } from 'framer-motion';
import { Users, Star, MessageCircle } from 'lucide-react';

const featureIconClass =
  'mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600/[0.08] text-blue-600 ring-1 ring-blue-600/10 dark:bg-blue-500/[0.1] dark:text-blue-400 dark:ring-blue-400/15';

const SocialProof = () => {
  return (
    <section className="relative overflow-hidden py-16 md:py-20 bg-zinc-50 dark:bg-zinc-900">
      <div
        className="pointer-events-none absolute inset-0 opacity-0 dark:opacity-100 dark:bg-[radial-gradient(ellipse_95%_55%_at_50%_-8%,rgba(59,130,246,0.1),transparent_58%),radial-gradient(ellipse_65%_45%_at_100%_85%,rgba(99,102,241,0.07),transparent_52%)]"
        aria-hidden
      />
      <div className="container relative z-10 px-4 md:px-6 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center space-y-12 md:space-y-16"
        >
          {/* Stats Row */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center space-y-4"
            >
              <div className={featureIconClass}>
                <Users className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="space-y-2">
                <div className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 md:text-2xl">
                  Active Community
                </div>
                <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-base">
                  Founders helping founders succeed
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center space-y-4"
            >
              <div className={featureIconClass}>
                <Star className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="space-y-2">
                <div className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 md:text-2xl">
                  Proven Methods
                </div>
                <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-base">
                  Battle-tested strategies that work
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center space-y-4"
            >
              <div className={featureIconClass}>
                <MessageCircle className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="space-y-2">
                <div className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 md:text-2xl">
                  Full Toolkit
                </div>
                <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-base">
                  13 AI tools in one subscription
                </p>
              </div>
            </motion.div>
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-md md:p-10 dark:border-zinc-600/45 dark:bg-zinc-800 dark:shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_14px_44px_-14px_rgba(0,0,0,0.5)]">
              <div className="text-center space-y-5">
                <div className="flex justify-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400/90 dark:text-amber-400/95" />
                  ))}
                </div>
                <blockquote className="text-lg font-medium leading-relaxed text-zinc-800 not-italic md:text-xl dark:text-zinc-100">
                  &ldquo;The playbooks give you a clear roadmap instead of trying to figure everything out yourself.
                  Finally, a step-by-step system that actually works.&rdquo;
                </blockquote>
                <div className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
                  From a LaunchGuide user
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProof; 